import { Component, Renderer2, ChangeDetectorRef } from '@angular/core';
import { AttachmentBus } from '../../shared/models/AttachmentBus';
import { InvoiceService } from '../../shared/services/invoice.service';
import { DomSanitizer} from '@angular/platform-browser';
import { GlobalsService } from '@app/cp/shared/services/globals.service';
import { BaseGridEditAttachmentsComponent } from '@app/cp/shared/base-components/base-grid-edit-attachments.component';
import { DateFunctions } from '@app/cp/shared/static/date-functions';
import { BaseMessageInfo } from '@app/cp/shared/models/BaseMessageInfo';
import { UploadComponent, FileState, UploadEvent, SelectEvent } from '@progress/kendo-angular-upload';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { FeatureFlagService } from "@app/cp/shared/services/feature-flag.service";
import { UseFeatureFlagSet, IFeatureFlagSet } from '@app/cp/shared/decorators/featureflags/featureflagset-decorator';
import { CommonFunctions } from '@app/cp/shared/static/common-functions';

@UseFeatureFlagSet()
@Component({
    selector: 'invoice-attachments',
    templateUrl: './invoice-attachments.component.html',
    styleUrls: ['./invoice-attachments.component.scss']
})
export class InvoiceAttachmentsComponent extends BaseGridEditAttachmentsComponent<AttachmentBus, null, InvoiceService> {
    compFeatureFlagSet: IFeatureFlagSet = this as any;

    constructor(globals: GlobalsService, renderer: Renderer2, cd: ChangeDetectorRef, sanitizer: DomSanitizer, svc_data: InvoiceService, featureFlagService: FeatureFlagService) {
        super(globals, renderer, cd, sanitizer, featureFlagService);
        this.svc_data = svc_data;
        this.className = "AttachmentBus";
        this.appCode = "InvoiceAttachments";
        this.disableCpSearchDialog();
    }

    public static virtualizationSettings = { itemHeight: 30, pageSize: 100 };

    createForm() {
        this.editForm = new UntypedFormGroup({
            'id': new UntypedFormControl(),
            'referenceId': new UntypedFormControl(),
            'attachmentName': new UntypedFormControl(),
            'attachmentType': new UntypedFormControl(),
            'addedDate': new UntypedFormControl(),
            'personId': new UntypedFormControl(),
            'attachmentDesc': new UntypedFormControl(),
            'visibleToCustomer': new UntypedFormControl(),
            'insertDt': new UntypedFormControl(),
            'insertUserId': new UntypedFormControl(),
            'updateDt': new UntypedFormControl(),
            'updateUserId': new UntypedFormControl(),
            'personName': new UntypedFormControl(),
            'attachment': new UntypedFormControl()
        });
    }

    setForm() {
        this.editForm.patchValue({
            id: this.selectedData.id,
            referenceId: this.isNew ? this._parentId : this.selectedData.referenceId,
            attachment: this.selectedData.attachment,
            attachmentName: this.selectedData.attachmentName,
            attachmentType: this.selectedData.attachmentType,
            addedDate: this.isNew ? this._parentId : this.selectedData.addedDate,
            personId: this.isNew ? this._parentId : this.selectedData.personId,
            attachmentDesc: this.selectedData.attachmentDesc,
            visibleToCustomer: this.selectedData.visibleToCustomer,
            personName: this.selectedData.personName,
            insertDt: this.selectedData.insertDt,
            insertUserId: this.selectedData.insertUserId,
            updateDt: this.selectedData.updateDt,
            updateUserId: this.selectedData.updateUserId
        });
    }

    setFromReferenceData(data: AttachmentBus) {
        data.personName = this.personName(data.personId);
        return data;
    }

    protected getChangedObject(data: AttachmentBus) {
        let busObj: AttachmentBus = new AttachmentBus(data);
        busObj.setParentId(this._parentId);
        busObj.setCommon(this.globals.userId, DateFunctions.getDateNow(), this.globals.companyId, this.globals.personId);
        busObj.setBeforeSave();
        return busObj;
    }

    save(data: AttachmentBus) {
        let busObj: AttachmentBus = this.getChangedObject(data);
        busObj.setParentId(this._parentId);
        busObj.setCommon(this.globals.userId, DateFunctions.getDateNow(), this.globals.companyId, this.globals.personId);

        this.dataLoading = true;

        this.svc_data.uploadAttachment(this.files, busObj).subscribe({
            next: (info: BaseMessageInfo) => {
                if (!info.saveError) {
                    let newBusObj: AttachmentBus = new AttachmentBus(info.primaryReturn);
                    this.saveSuccess(newBusObj);

                    this.svc_data.getAttachments(this._parentId).subscribe(data => {
                        this.gridData = data;
                        this.cd.detectChanges();
                    });
                } else {
                    this.saveError(info.primaryReturn as AttachmentBus, info.message);
                }
                this.clearUpload();
                this.dataLoading = false;
                this.cd.detectChanges();
            },
            error: (error) => {
                this.dataLoading = false;
                this.cd.detectChanges();
            }
        });
    }

    deleteIndividual(data: AttachmentBus) {
        if (data && data.id) {
            this.svc_data.deleteInvoiceAttachment(data.id).subscribe({
                next: (info: BaseMessageInfo) => {
                    if (!info.saveError) {
                        this.svc_data.getAttachments(this._parentId).subscribe(gridData => {
                            this.gridData = gridData;
                            this.cd.detectChanges();
                        });
                    } else {
                        this.deleteError(data, info.message);
                    }
                },
                error: (error) => {
                    this.deleteError(data, 'Error deleting attachment');
                }
            });
        } else {
            this.closeEditor();
        }
    }

    protected override removeLocal(data: AttachmentBus): void {
        if (this.gridData && Array.isArray(this.gridData)) {
            const index = this.gridData.findIndex(item => item.id === data.id);
            if (index > -1) {
                this.gridData.splice(index, 1);
                this.cd.detectChanges();
            }
        }
    }

    override selectEventHandler(e: SelectEvent) {
        super.selectEventHandler(e);
        CommonFunctions.patchEditFormWithAttachment(this.files?.[0], this.editForm);
    }

    public remove(upload: UploadComponent, uid: string) {
        upload.removeFilesByUid(uid);
    }

    public showButton(state: FileState): boolean {
        return (state === FileState.Selected) ? true : false;
    }

    uploadEventHandler(e: UploadEvent) {
        this.setDirty();
        this.setDirty();
    }

    get showId(): boolean {
        return this.editedRowIndex == undefined && this.isNew == false ? true : false;
    }

    view(data: any) {
        let busObj: AttachmentBus = new AttachmentBus(data);
        super.view(busObj);
    }
}